import React from "react";
import EditIcon from "@mui/icons-material/Edit";
import FolderSpecialIcon from "@mui/icons-material/FolderSpecial";
import PlaceIcon from "@mui/icons-material/Place";
import {
  Avatar,
  Box,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { Label } from "src/components";
import { ApiUrl } from "src/services/apiUrls";

const RootStyle = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
  boxShadow: theme.customShadows?.z8 || "0 8px 20px rgba(0,0,0,0.08)",
}));

const ThumbStyle = styled("img")(({ theme }) => ({
  width: 54,
  height: 54,
  flexShrink: 0,
  objectFit: "cover",
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default,
}));

const getImageUrl = (image) => {
  if (!image) return "";
  if (/^https?:\/\//i.test(image)) return image;
  return `${ApiUrl.IMAGE_BASE_URL}${image}`;
};

export default function ProjectCard({
  item,
  row,
  isLoading,
  serialNumber,
  retrieveValue,
}) {
  const theme = useTheme();
  const data = item || row || {};
  const projectId = data?._id || data?.id;
  const isActive = data?.is_active === true || data?.is_active === 1;
  const imageUrl = getImageUrl(data?.image);

  return (
    <RootStyle
      sx={{
        borderLeft: `6px solid ${
          isLoading
            ? theme.palette.divider
            : theme.palette[isActive ? "success" : "error"].main
        }`,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        {isLoading ? (
          <Skeleton variant="rounded" width={54} height={54} />
        ) : imageUrl ? (
          <ThumbStyle alt={data?.name || "Project"} src={imageUrl} />
        ) : (
          <Avatar
            variant="rounded"
            sx={{ width: 54, height: 54, flexShrink: 0 }}
          >
            <FolderSpecialIcon fontSize="small" />
          </Avatar>
        )}

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {isLoading ? <Skeleton width={32} /> : `#${serialNumber}`}
          </Typography>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {isLoading ? <Skeleton width="80%" /> : data?.name || "Untitled"}
          </Typography>

          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.75 }}>
            {isLoading ? (
              <Skeleton variant="circular" width={18} height={18} />
            ) : (
              <PlaceIcon fontSize="small" color="action" />
            )}
            <Typography variant="caption" color="text.secondary" noWrap>
              {isLoading ? <Skeleton width={80} /> : data?.location || "-"}
            </Typography>
          </Stack>

          {isLoading ? (
            <Skeleton width="90%" />
          ) : data?.description ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.75,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {data.description}
            </Typography>
          ) : null}

          <Box sx={{ mt: 1 }}>
            {isLoading ? (
              <Skeleton width={64} height={24} />
            ) : (
              <Label
                variant={theme.palette.mode === "light" ? "ghost" : "filled"}
                color={isActive ? "success" : "error"}
              >
                {isActive ? "Active" : "in-active"}
              </Label>
            )}
          </Box>
        </Box>

        {isLoading ? (
          <Skeleton variant="circular" width={34} height={34} />
        ) : (
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => retrieveValue?.(data)}
              disabled={!projectId}
              sx={{ flexShrink: 0 }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </RootStyle>
  );
}
